import { FC, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { OrganizationStatus } from "@/constants";

interface SyncingBannerProps {
  status: OrganizationStatus;
}

export const SyncingBanner: FC<SyncingBannerProps> = ({ status }) => {
  const toastIdRef = useRef<string | number | null>(null);
  const progressRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (status === OrganizationStatus.SYNCING) {
      if (!toast.isActive(toastIdRef.current as string)) {
        toastIdRef.current = toast.info(
          <div className="flex items-start space-x-3">
            {/* <div className="mt-1 animate-spin">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            </div> */}
            <div className="space-y-2">
              <p className="font-bold text-lg mb-2"> Hang tight! ⏳</p>
              <p className="text-sm">
                We're syncing your <strong>customer</strong> and{" "}
                <strong>transaction</strong> data from Clover. This may take up
                to <strong>2 hours</strong> depending on the data load.
              </p>
            </div>
          </div>,
          {
            position: "top-center",
            theme: "colored",
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            hideProgressBar: false,
            closeButton: false,
            progress: 0,
            className:
              "bg-red-100 border border-blue-400 text-blue-800 w-[900px] max-w-full shadow-md rounded-md p-4"
          }
        );

        // Start fake progress
        intervalRef.current = setInterval(() => {
          const current = progressRef.current;
          let increment = 0;

          if (current < 0.3) increment = 0.005;
          else if (current < 0.6) increment = 0.003;
          else if (current < 0.9) increment = 0.001;

          if (increment > 0) {
            progressRef.current = Math.min(current + increment, 0.75);
            toast.update(toastIdRef.current!, {
              progress: progressRef.current
            });
          }
        }, 1000);
      }
    }

    if (status === OrganizationStatus.ACTIVE && toastIdRef.current) {
      // Clear interval and complete the progress
      if (intervalRef.current) clearInterval(intervalRef.current);

      toast.update(toastIdRef.current, {
        render: (
          <div className="flex items-start space-x-3">
            <div className="mt-1 text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1.293-5.707a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L9 10.586 7.293 8.879a1 1 0 00-1.414 1.414l2 2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-sm">Sync complete!</p>
              <p className="text-sm">All your data is now up to date.</p>
            </div>
          </div>
        ),
        progress: 1,
        autoClose: 3000
      });

      toastIdRef.current = null;
      progressRef.current = 0;
      intervalRef.current = null;
    }
  }, [status]);

  return <ToastContainer />;
};
